/**
 * TIRSummaryTable - TIR Statistics Table for Dashboard (Part 2)
 *
 * Displays structured TIR data:
 * Target Range, TIR%, TBR%, TAR%, Average Glucose, Data Coverage
 */

import {
  Box,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from '@mui/material'
import React, { useMemo } from 'react'
import { useStore, useTranslate } from 'react-admin'
import { useDeviceMetrics } from '../../hooks/useDeviceMetrics'
import { useGlucoseDisplay } from '../../hooks/useGlucoseDisplay'
import { GLUCOSE_THRESHOLDS, TIR_ZONE_COLORS, TIRSummaryStats } from '../../types/dashboard'
import { SHARED_METRICS_PER_PAGE } from '../../utils/paginationUtils'
import { calculateTIRStats } from '../../utils/tirStats'

export interface TIRSummaryTableProps {
  deviceId?: string | number
  petId?: string | number
  startDate?: Date
  endDate?: Date
  targetMin?: number
  targetMax?: number
  highMax?: number
}

export const TIRSummaryTable: React.FC<TIRSummaryTableProps> = React.memo(
  ({
    deviceId: propDeviceId,
    petId: propPetId,
    startDate,
    endDate,
    targetMin,
    targetMax,
    highMax,
  }) => {
    const translate = useTranslate()
    const [storeDeviceId] = useStore<any>('selectedDeviceId')
    const [storePetId] = useStore<any>('selectedPetId')
    const { glucoseUnit, convertValue, formatValue } = useGlucoseDisplay()
    const deviceId = propDeviceId ?? storeDeviceId
    const petId = propPetId ?? storePetId

    // Use the unified metrics hook that handles virtual devices
    const {
      data: metricsData = [],
      isLoading,
      error,
    } = useDeviceMetrics({
      deviceId,
      petId,
      startDate,
      endDate,
      sort: { field: 'timestamp', order: 'ASC' },
      pagination: { page: 1, perPage: SHARED_METRICS_PER_PAGE },
    })

    // Calculate TIR statistics
    const stats = useMemo((): TIRSummaryStats | null => {
      const computedStats = calculateTIRStats(
        metricsData,
        startDate,
        endDate,
        targetMin,
        targetMax,
        highMax,
      )
      if (!computedStats) return null

      return {
        targetRange: {
          min: targetMin ?? GLUCOSE_THRESHOLDS.TARGET_MIN,
          max: targetMax ?? GLUCOSE_THRESHOLDS.TARGET_MAX,
        },
        tir: computedStats.tir,
        tbr: computedStats.tbr,
        tar: computedStats.tar,
        avgGlucose: computedStats.avgGlucose,
        dataCoverage: computedStats.dataCoverage,
        periodStart: startDate || new Date(),
        periodEnd: endDate || new Date(),
      }
    }, [metricsData, startDate, endDate, targetMin, targetMax, highMax])

    if (isLoading) {
      return (
        <Box sx={{ p: 2 }}>
          <Skeleton variant="rectangular" height={200} />
        </Box>
      )
    }

    if (error) {
      return (
        <Box sx={{ p: 2 }}>
          <Typography color="error">{translate('pos.dashboard.error_loading_data')}</Typography>
        </Box>
      )
    }

    if (!stats) {
      return (
        <Box sx={{ p: 2 }}>
          <Typography color="text.secondary">
            {translate('pos.dashboard.no_data_available')}
          </Typography>
        </Box>
      )
    }

    const highlightBackground = 'rgba(34, 197, 94, 0.16)'
    const highlightBorder = '1px solid rgba(34, 197, 94, 0.34)'
    const highlightLabelColor = '#15803D'

    const rows = [
      {
        id: 'target-range',
        label: translate('pos.glucose.target_range'),
        value: `${formatValue(stats.targetRange.min)} - ${formatValue(stats.targetRange.max)} ${glucoseUnit}`,
        color: TIR_ZONE_COLORS.target,
      },
      {
        id: 'time-in-range',
        label: translate('pos.glucose.time_in_range'),
        value: `${stats.tir.toFixed(1)}%`,
        color: TIR_ZONE_COLORS.target,
        highlight: true,
      },
      {
        id: 'time-below-range',
        label: translate('pos.glucose.time_below_range'),
        value: `${stats.tbr.toFixed(1)}%`,
        color: stats.tbr > 4 ? TIR_ZONE_COLORS.low : undefined, // Highlight if >4% (clinical concern)
      },
      {
        id: 'time-above-range',
        label: translate('pos.glucose.time_above_range'),
        value: `${stats.tar.toFixed(1)}%`,
        color: stats.tar > 25 ? TIR_ZONE_COLORS.high : undefined, // Highlight if >25% (clinical concern)
      },
      {
        id: 'average-glucose',
        label: translate('pos.glucose.average_glucose'),
        value: `${formatValue(stats.avgGlucose)} ${glucoseUnit}`,
      },
      {
        id: 'data-coverage',
        label: translate('pos.glucose.data_coverage'),
        value: `${stats.dataCoverage.toFixed(1)}%`,
        color: stats.dataCoverage < 70 ? TIR_ZONE_COLORS.high : undefined, // Highlight if <70%
      },
    ]

    return (
      <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e0e0e0' }}>
        <Table size="small">
          <TableBody>
            {rows.map((row) => (
              <TableRow
                key={row.id}
                sx={{
                  '&:last-child td, &:last-child th': { border: 0 },
                  backgroundColor: row.highlight ? highlightBackground : undefined,
                }}
              >
                <TableCell
                  component="th"
                  scope="row"
                  sx={{
                    fontWeight: row.highlight ? 800 : 500,
                    color: row.highlight ? highlightLabelColor : 'inherit',
                    backgroundColor: row.highlight ? highlightBackground : undefined,
                    borderTop: row.highlight ? highlightBorder : undefined,
                    borderBottom: row.highlight ? highlightBorder : undefined,
                    borderLeft: row.highlight ? `4px solid ${TIR_ZONE_COLORS.target}` : undefined,
                    borderRight: row.highlight ? 'none' : undefined,
                    borderTopLeftRadius: row.highlight ? 12 : 0,
                    borderBottomLeftRadius: row.highlight ? 12 : 0,
                    py: row.highlight ? 1.4 : 0.75,
                    pl: row.highlight ? 2 : 2,
                    boxShadow: row.highlight ? '0 6px 14px rgba(34, 197, 94, 0.08)' : 'none',
                  }}
                >
                  {row.label}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    fontWeight: row.highlight ? 900 : 500,
                    fontSize: row.highlight ? '1.5rem' : 'inherit',
                    color: row.color || 'inherit',
                    backgroundColor: row.highlight ? highlightBackground : undefined,
                    border: row.highlight ? highlightBorder : undefined,
                    borderLeft: row.highlight ? 'none' : undefined,
                    borderTopRightRadius: row.highlight ? 12 : 0,
                    borderBottomRightRadius: row.highlight ? 12 : 0,
                    py: row.highlight ? 1.4 : 0.75,
                    boxShadow: row.highlight ? '0 6px 14px rgba(34, 197, 94, 0.12)' : 'none',
                  }}
                >
                  {row.value}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    )
  },
)

export default TIRSummaryTable
